import { useState } from 'react';
import { KoobButton, KoobInputDateRange, KoobModal } from '@koob/ui';
import KoobTitle from '../Koob/KoobTitle';
import { useTranslation } from 'react-i18next';
import { dayjs, toDateString } from '@koob/dates';
import GuestsRoomsPicker, {
  newGuestsRoomsComposition,
  getEffectiveComposition,
  isGuestsRoomsCompositionValid,
  toHotelSearchRooms,
} from '../GuestsRoomsPicker';

const toDate = value => (value ? (value?.toDate?.() ?? new Date(value)) : null);

export default function HotelGetAvailabilityModal({
  open,
  setOpen,
  onSubmit,
  initialRooms = null,
  initialCheckIn = null,
  initialCheckOut = null,
}) {
  const { t } = useTranslation('hotel');

  const [guestsRooms, setGuestsRooms] = useState(newGuestsRoomsComposition());
  const [checkIn, setCheckIn] = useState(initialCheckIn);
  const [checkOut, setCheckOut] = useState(initialCheckOut);
  const [prevOpen, setPrevOpen] = useState(false);

  if (open && !prevOpen) {
    if (initialRooms?.length > 0) {
      const totalAdults = initialRooms.reduce((s, r) => s + (r.adults ?? 0), 0);
      const totalChildren = initialRooms.reduce(
        (s, r) => s + (r.children ?? 0),
        0,
      );
      const allBirthdates = initialRooms.flatMap(r =>
        (r.childrenBirthdates || []).slice(0, r.children),
      );
      setGuestsRooms({
        adults: totalAdults,
        children: totalChildren,
        childrenBirthdates: allBirthdates,
        rooms: initialRooms.length > 1 ? initialRooms : null,
      });
    } else {
      setGuestsRooms(newGuestsRoomsComposition());
    }
    setCheckIn(initialCheckIn);
    setCheckOut(initialCheckOut);
    setPrevOpen(true);
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  const canSubmit =
    checkIn &&
    checkOut &&
    dayjs(checkOut).isAfter(checkIn) &&
    isGuestsRoomsCompositionValid(guestsRooms) &&
    (guestsRooms?.adults ?? 0) > 0;

  const handleSubmit = () => {
    onSubmit({
      rooms: toHotelSearchRooms(getEffectiveComposition(guestsRooms)),
      startAt: toDateString(checkIn),
      endAt: toDateString(checkOut),
    });
    setOpen(false);
  };

  return (
    <KoobModal
      open={open}
      setOpen={setOpen}
      widthClass="sm:max-w-2xl"
      overflow="visible"
    >
      <KoobTitle className="text-center">
        {t('getAvailabilityModal.title')}
      </KoobTitle>

      <p className="text-center text-gray-500 text-sm mt-1 px-6">
        {t('getAvailabilityModal.description')}
      </p>

      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between border rounded-lg px-4 py-3">
          <span className="text-gray-700 font-medium">
            {t('getAvailabilityModal.dates')}
          </span>
          <KoobInputDateRange
            presets={[]}
            value={{
              from: toDate(checkIn) ?? undefined,
              to: toDate(checkOut) ?? undefined,
            }}
            onChange={range => {
              setCheckIn(range?.from ?? null);
              setCheckOut(range?.to ?? null);
            }}
            minDate={dayjs().toDate()}
          />
        </div>

        <GuestsRoomsPicker
          value={guestsRooms}
          onChange={setGuestsRooms}
          translationNamespace="hotel"
        />

        <div className="flex justify-end">
          <KoobButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {t('getAvailabilityModal.title')}
          </KoobButton>
        </div>
      </div>
    </KoobModal>
  );
}
